// Collection Tabs section JS. Drives tab switching, paginated grid display
// with infinite-loop arrow nav, sort, swipe, and breakpoint-aware page sizing.
//
// Cohesion notes:
// - Section element is found via `[data-collection-tabs]` attribute (not
//   `[class*="collection-tabs-"]` partial-match — that selector matched any
//   element with a class starting with that prefix).
// - Button class hooks are `.swiper-btn-prev` / `.swiper-btn-next` to align
//   with the rest of the codebase. The buttons aren't Swiper navigation —
//   their click handlers are wired manually here — but the naming convention
//   keeps slider-nav classes consistent across sections.
// - Buttons hide via `.is-nav-hidden` class on the panel when the current
//   viewport's pagination math yields <= 1 page. This per-panel tracking
//   matters because tabs can have different product counts.

(function () {
  'use strict';

  class CollectionTabsSection {
    constructor(container) {
      this.container = container;
      this.tabButtons = Array.from(container.querySelectorAll('.collection-tabs__tab'));
      this.panels = Array.from(container.querySelectorAll('.collection-tabs__panel'));
      this.currentBreakpoint = this.getBreakpoint();
      this.resizeHandler = null;
      this.init();
    }

    init() {
      this.setupTabs();
      const firstPanel = this.panels[0];
      if (firstPanel) {
        this.initializePanel(firstPanel);
      }

      // Recalculate pagination on breakpoint changes (rotation, resize).
      // Stored on the instance so destroy() can remove it cleanly.
      let resizeTimer;
      this.resizeHandler = () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          const activePanel = this.container.querySelector('.collection-tabs__panel.is-active');
          if (!activePanel) return;
          const newBreakpoint = this.getBreakpoint();
          const currentPage = activePanel.currentPage || 1;

          if (newBreakpoint !== this.currentBreakpoint) {
            this.currentBreakpoint = newBreakpoint;
            this.initializePanel(activePanel, true);
          } else {
            // Same breakpoint, different width: the track's px column width
            // is stale even though the page count hasn't changed.
            this.layoutPanel(activePanel);
          }
          this.showPage(activePanel, Math.min(currentPage, activePanel.totalPages), true);
        }, 250);
      };
      window.addEventListener('resize', this.resizeHandler);
    }

    destroy() {
      if (this.resizeHandler) {
        window.removeEventListener('resize', this.resizeHandler);
        this.resizeHandler = null;
      }
    }

    getBreakpoint() {
      const width = window.innerWidth;
      if (width < 750) return 'mobile';
      if (width < 990) return 'tablet';
      return 'desktop';
    }

    getPerRow(grid) {
      const w = window.innerWidth;
      if (w < 750) return parseInt(grid.dataset.productsPerRowMobile);
      if (w < 990) return parseInt(grid.dataset.productsPerRowTablet);
      return parseInt(grid.dataset.productsPerRowDesktop);
    }

    getProductsPerPage(grid) {
      const maxRows = parseInt(grid.dataset.maxRows) || 1;
      return this.getPerRow(grid) * maxRows;
    }

    // Sizes the track's columns off the viewport's measured width and
    // records how far one page travels. Percentages can't do this in CSS:
    // the track's own width comes from its columns, so a % column width
    // would be circular. Re-run on any resize, not just breakpoint changes.
    layoutPanel(panel) {
      const grid = panel.querySelector('.collection-tabs__grid');
      const track = panel.querySelector('.collection-tabs__track');
      if (!grid || !track) return;

      const perRow = this.getPerRow(grid);
      const maxRows = parseInt(grid.dataset.maxRows) || 1;
      // Read the gap back from CSS rather than re-deriving the breakpoint
      // cascade here — one source of truth for the spacing.
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      const viewport = grid.clientWidth;
      if (!viewport) return;

      track.style.setProperty('--max-rows', maxRows);
      track.style.setProperty('--col-w', (viewport - (perRow - 1) * gap) / perRow + 'px');
      // A page is the full viewport plus the gap that follows it.
      panel.pageStride = viewport + gap;
    }

    sortProducts(container, cards, sortBy) {
      const sorted = [...cards];
      switch (sortBy) {
        case 'metafield_desc':
          sorted.sort((a, b) => (parseFloat(b.dataset.metafieldValue) || 0) - (parseFloat(a.dataset.metafieldValue) || 0));
          break;
        case 'metafield_asc':
          sorted.sort((a, b) => (parseFloat(a.dataset.metafieldValue) || 0) - (parseFloat(b.dataset.metafieldValue) || 0));
          break;
        case 'price_asc':
          sorted.sort((a, b) => parseFloat(a.dataset.price) - parseFloat(b.dataset.price));
          break;
        case 'price_desc':
          sorted.sort((a, b) => parseFloat(b.dataset.price) - parseFloat(a.dataset.price));
          break;
        case 'title_asc':
          sorted.sort((a, b) => a.dataset.title.localeCompare(b.dataset.title));
          break;
        case 'title_desc':
          sorted.sort((a, b) => b.dataset.title.localeCompare(a.dataset.title));
          break;
        case 'created_asc':
          sorted.sort((a, b) => parseInt(a.dataset.created) - parseInt(b.dataset.created));
          break;
        case 'created_desc':
          sorted.sort((a, b) => parseInt(b.dataset.created) - parseInt(a.dataset.created));
          break;
        default:
          return;
      }
      sorted.forEach((card) => container.appendChild(card));
    }

    setupTabs() {
      this.tabButtons.forEach((button) => {
        button.addEventListener('click', () => {
          this.switchTab(button.dataset.tabId);
        });
      });
    }

    switchTab(tabId) {
      this.tabButtons.forEach((btn) => {
        const isActive = btn.dataset.tabId === tabId;
        btn.classList.toggle('is-active', isActive);
        btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
      this.panels.forEach((panel) => {
        const isActive = panel.dataset.panelId === tabId;
        panel.classList.toggle('is-active', isActive);
        if (isActive) {
          this.initializePanel(panel);
        }
      });
    }

    initializePanel(panel, skipInitialShow = false) {
      const grid = panel.querySelector('.collection-tabs__grid');
      if (!grid) return;

      const track = panel.querySelector('.collection-tabs__track');
      const cards = Array.from(panel.querySelectorAll('.product-card'));
      const sortBy = grid.dataset.sortBy;
      if (sortBy && track) {
        this.sortProducts(track, cards, sortBy);
      }

      this.layoutPanel(panel);

      const productsPerPage = this.getProductsPerPage(grid);
      const totalPages = Math.ceil(cards.length / productsPerPage);
      panel.totalPages = totalPages;

      const totalSpan = panel.querySelector('.collection-tabs__total-pages');
      if (totalSpan) {
        totalSpan.textContent = totalPages;
      }

      // Hide nav buttons (and the page indicator) when there's only one
      // page worth of products at the current viewport. The panel-level
      // class gates both via CSS — buttons display:none, indicator stays
      // hidden because there's nothing to indicate.
      panel.classList.toggle('is-nav-hidden', totalPages <= 1);

      // Wire arrow buttons. Replace-with-clone resets any prior listeners
      // so re-initialization (tab switch, breakpoint change) doesn't stack
      // duplicate handlers.
      const oldPrev = panel.querySelector('.swiper-btn-prev');
      const oldNext = panel.querySelector('.swiper-btn-next');
      if (oldPrev && oldNext) {
        oldPrev.replaceWith(oldPrev.cloneNode(true));
        oldNext.replaceWith(oldNext.cloneNode(true));

        const prev = panel.querySelector('.swiper-btn-prev');
        const next = panel.querySelector('.swiper-btn-next');

        prev.addEventListener('click', () => this.step(panel, -1));
        next.addEventListener('click', () => this.step(panel, 1));
      }

      // Mini-buttons inside the page indicator (mobile-only via CSS).
      // Same pagination logic as the big buttons; they're separate DOM
      // elements with separate listeners since CSS visibility differs.
      const oldMiniPrev = panel.querySelector('.collection-tabs__mini-btn--prev');
      const oldMiniNext = panel.querySelector('.collection-tabs__mini-btn--next');
      if (oldMiniPrev && oldMiniNext) {
        oldMiniPrev.replaceWith(oldMiniPrev.cloneNode(true));
        oldMiniNext.replaceWith(oldMiniNext.cloneNode(true));

        const miniPrev = panel.querySelector('.collection-tabs__mini-btn--prev');
        const miniNext = panel.querySelector('.collection-tabs__mini-btn--next');

        miniPrev.addEventListener('click', () => this.step(panel, -1));
        miniNext.addEventListener('click', () => this.step(panel, 1));
      }

      this.setupTouchSwipe(grid, panel);

      if (!skipInitialShow) {
        this.showPage(panel, 1, true);
      }
    }

    step(panel, delta) {
      const target = (panel.currentPage || 1) + delta;
      if (target < 1 || target > panel.totalPages) return;
      this.showPage(panel, target);
    }

    showPage(panel, pageNum, instant = false) {
      const track = panel.querySelector('.collection-tabs__track');
      if (!track) return;

      panel.currentPage = pageNum;
      const from = panel.trackOffset || 0;
      const offset = (pageNum - 1) * (panel.pageStride || 0);
      panel.trackOffset = offset;

      // The resting position is always a plain inline transform, and the
      // animation below only plays the travel between two resting states.
      // That way a dropped animation (reduced motion, a backgrounded tab
      // whose timeline is frozen, no WAAPI) still leaves the track at the
      // right offset — a bare CSS transition would sit at the old one.
      track.style.transform = 'translate3d(' + -offset + 'px, 0, 0)';

      const reduceMotion =
        window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (!instant && !reduceMotion && from !== offset && track.animate) {
        const speed = parseFloat(getComputedStyle(track).getPropertyValue('--tabs-slide-speed')) || 900;
        if (panel.slideAnimation) panel.slideAnimation.cancel();
        panel.slideAnimation = track.animate(
          [
            { transform: 'translate3d(' + -from + 'px, 0, 0)' },
            { transform: 'translate3d(' + -offset + 'px, 0, 0)' },
          ],
          { duration: speed, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' }
        );
      }

      const currentSpan = panel.querySelector('.collection-tabs__current-page');
      if (currentSpan) currentSpan.textContent = pageNum;

      this.updateNavState(panel);
    }

    updateNavState(panel) {
      const atStart = (panel.currentPage || 1) <= 1;
      const atEnd = (panel.currentPage || 1) >= panel.totalPages;
      panel.querySelectorAll('.swiper-btn-prev, .collection-tabs__mini-btn--prev').forEach((el) => {
        el.classList.toggle('swiper-button-disabled', atStart);
      });
      panel.querySelectorAll('.swiper-btn-next, .collection-tabs__mini-btn--next').forEach((el) => {
        el.classList.toggle('swiper-button-disabled', atEnd);
      });
    }

    setupTouchSwipe(grid, panel) {
      if (!grid) return;
      // Called on every tab switch / resize; bind once so one swipe = one step.
      if (grid.dataset.swipeBound) return;
      grid.dataset.swipeBound = '1';
      let startX = 0;
      const minDistance = 50;

      grid.addEventListener(
        'touchstart',
        (e) => {
          startX = e.changedTouches[0].screenX;
        },
        { passive: true }
      );
      grid.addEventListener(
        'touchend',
        (e) => {
          const endX = e.changedTouches[0].screenX;
          const diff = startX - endX;
          if (Math.abs(diff) <= minDistance) return;
          // Don't swipe if pagination isn't needed.
          if (panel.totalPages <= 1) return;

          this.step(panel, diff > 0 ? 1 : -1);
        },
        { passive: true }
      );
    }
  }

  // Track instances by section element so design-mode reloads don't
  // orphan listeners (the previous implementation created a new instance
  // on every tab block-select, leaking resize handlers).
  const instances = new WeakMap();

  function initSection(sectionEl) {
    if (!sectionEl) return;
    const existing = instances.get(sectionEl);
    if (existing) {
      existing.destroy();
    }
    instances.set(sectionEl, new CollectionTabsSection(sectionEl));
  }

  function initAll(root = document) {
    root.querySelectorAll('[data-collection-tabs]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  // Theme editor lifecycle
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => {
      initAll(event.target);
    });
    document.addEventListener('shopify:section:unload', (event) => {
      const section = event.target.querySelector('[data-collection-tabs]');
      if (section) {
        const instance = instances.get(section);
        if (instance) {
          instance.destroy();
          instances.delete(section);
        }
      }
    });
    document.addEventListener('shopify:block:select', (event) => {
      const section = event.target.closest('[data-collection-tabs]');
      if (!section) return;
      const instance = instances.get(section);
      if (instance && event.target.classList.contains('collection-tabs__tab')) {
        instance.switchTab(event.target.dataset.tabId);
      }
    });
  }
})();
